
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import { motion } from "framer-motion";

const faqs = [
  {
    q: "What does TapToTable actually do?",
    a: "TapToTable gives your restaurant a focused mobile page designed to turn online interest into visits, calls, directions, orders, promotion claims, and event actions. You can promote special offers, upcoming events, or featured menu items, and give customers a clear next step. Manage your page and customer activity from your secure, easy-to-use TapToTable Dashboard.",
  },
  {
    q: "How much does TapToTable cost?",
    a: "TapToTable costs $59 per month for your first restaurant location after activation. Each additional location under the same ownership is $39 per month. Your plan includes your hosted TapToTable Page, a secure, easy-to-use dashboard, Promotions, Events, and Featured Items, restaurant details, photos, links, supported customer activity analytics, hosting, and ongoing support. There are no setup fees or long-term contracts, and you can cancel anytime.",
  },
  {
    q: "What if I own more than one restaurant location?",
    a: "TapToTable offers multi-location pricing for restaurants under the same ownership. Your first location is $59/month, and each additional location is $39/month. Each restaurant gets its own TapToTable Page, login, and dashboard so you can manage its promotions, events, featured items, and customer information separately. Contact TapToTable to arrange the additional-location rate for your other restaurants.",
  },
  {
    q: "Can different restaurant brands under the same owner qualify for the additional-location rate?",
    a: "If you own multiple restaurant locations or restaurant brands, contact TapToTable with the details. We can review the common ownership and arrange eligible additional locations at $39/month each. The additional-location rate is not a standalone subscription for unrelated new customers.",
  },
  {
    q: "Do I need a separate dashboard for each location?",
    a: "Yes. For now, every restaurant location has its own TapToTable Page, login, and secure, easy-to-use dashboard. This lets each location manage its own promotions, events, featured menu items, photos, links, and customer information independently. A combined multi-location dashboard is not currently available.",
  },
  {
    q: "Do I pay before my page is built?",
    a: "No. We build your TapToTable Page first so you can review the customer experience and request changes. You activate the page and begin the applicable monthly subscription only when you are satisfied and ready to use it.",
  },
  {
    q: "What happens after I submit my restaurant information?",
    a: "We use the restaurant information, photos, menu, website, social profiles, and links you provide to build your TapToTable Page. When it is ready, you receive a private review link where you can view the page like a customer, request changes, and activate it when you are ready.",
  },
  {
    q: "How long does it take to build my page?",
    a: "Most TapToTable Pages can be prepared within 24 to 48 hours after we receive enough restaurant information, photos, and links. Timing may vary depending on the available information and requested customization.",
  },
  {
    q: "Can I request changes before activation?",
    a: "Yes. You can review your completed TapToTable Page and request changes before activation. The goal is to make sure the page accurately represents your restaurant before customers see it.",
  },
  {
    q: "What types of restaurants can TapToTable work with?",
    a: "TapToTable works with pizzerias, burger restaurants, taco shops, cafés, coffee shops, breakfast and brunch locations, bakeries, fine dining, barbecue, seafood, sushi, diners, bars and grills, food trucks, dessert shops, and many other independent food businesses. Your page theme, campaign, wording, buttons, images, and links can be tailored to fit your brand.",
  },
  {
    q: "What can I promote with TapToTable?",
    a: "TapToTable offers three campaign types: Offers & Promotions, Events, and Featured Items. Run limited-time offers or loyalty rewards, advertise live music or special events, or showcase signature dishes and new menu items without requiring discounts or claim codes.",
  },
  {
    q: "What is a Featured Item campaign?",
    a: "Featured Items let you spotlight a signature dish, popular menu favorite, seasonal item, or new menu launch. Your customer page displays the featured item photo, name, description, and an action button such as View Menu. Customers can explore the item and visit your restaurant without claiming an offer or receiving a redemption code.",
  },
  {
    q: "Can I create an Event instead of an Offer?",
    a: "Yes. Your secure, easy-to-use TapToTable Dashboard lets you choose an Offer / Promotion, Event, or Featured Item. For events, you can enter the event name, date, time, description, and a primary action such as RSVP, Reserve Table, Get Tickets, Join Guest List, or another custom action.",
  },
  {
    q: "Can I switch between Promotions, Events, and Featured Items?",
    a: "Yes. You can switch between all three campaign types from your TapToTable Dashboard. Update your promotion, publish an upcoming event, or showcase a featured menu item whenever your restaurant has something new to promote.",
  },
  {
    q: "Do customers need to download an app or create an account?",
    a: "No. Customers open your TapToTable Page in their mobile browser. They can view your restaurant information, use your customer action links, explore events and featured items, or claim an available promotion without downloading an app or creating an account.",
  },
  {
    q: "Where can I share my TapToTable link?",
    a: "You can share your TapToTable link through Instagram and Facebook posts, social bios, stories, Google Business Profile, your website, text messages, email, digital ads, QR codes, printed materials, and anywhere customers discover your business.",
  },
  {
    q: "Can TapToTable connect to my menu and online ordering?",
    a: "Yes. Your TapToTable Page can connect customers to your menu, online ordering page, website, phone number, directions, reservations, and other important restaurant links.",
  },
  {
    q: "Can I manage my dashboard from a phone or computer?",
    a: "Yes. Your secure, easy-to-use TapToTable Dashboard is available from a phone or computer. You can update your campaigns, restaurant details, photos, and customer action links without technical skills or rebuilding your website.",
  },
  {
    q: "Can I change my campaign or restaurant information later?",
    a: "Yes. After activation, you can use your TapToTable Dashboard to create and update Promotions, Events, and Featured Items, change customer action buttons and links, and update restaurant details, photos, hours, website, menu, ordering links, and other supported information.",
  },
  {
    q: "What analytics are included?",
    a: "Your TapToTable Dashboard includes supported customer activity analytics such as page visits and promotion claims. Event actions are also tracked for the main event button and supported actions. Featured Item campaigns can show page activity, but dedicated Featured Item button-click tracking is not currently available. Available metrics vary by campaign type.",
  },
  {
    q: "How does TapToTable prevent inflated analytics?",
    a: "TapToTable uses browser-based duplicate protection for supported offer claims and event actions to reduce repeated interactions from the same visitor being counted multiple times. This helps provide more meaningful activity reporting, although no tracking system can completely eliminate duplicate activity.",
  },
  {
    q: "Can I cancel anytime?",
    a: "Yes. TapToTable is billed monthly with no long-term contract. You can cancel your recurring plan at any time, and future renewals will stop according to the applicable billing terms. Each restaurant location has its own subscription.",
  },
];

const objections = [
  {
    q: "We already have a website.",
    a: "TapToTable does not replace your website. It gives social-media visitors a faster, mobile-friendly path to your current Promotion, Event, or Featured Item, along with your menu, ordering link, phone number, directions, and restaurant details.",
  },
  {
    q: "We do not want to discount our food.",
    a: "You do not have to discount your food. Featured Item campaigns let you spotlight signature dishes, best sellers, and new menu items with photos, descriptions, and customer action buttons. You can also promote events, reservations, tastings, and other reasons to visit without offering a discount or requiring claim codes.",
  },
  {
    q: "We can already place our website in our Instagram bio.",
    a: "You can, but a standard restaurant website often requires visitors to search for your current Promotion, Event, Featured Item, ordering link, phone number, or directions. A TapToTable Page puts your most important customer actions in one focused mobile experience.",
  },
  {
    q: "We are not sure it will bring customers.",
    a: "No marketing platform can guarantee restaurant traffic. TapToTable gives customers a clearer path from online interest to action and provides supported activity analytics so you can evaluate how people respond to your restaurant's page.",
  },
  {
    q: "We do not have time to manage another platform.",
    a: "Your secure, easy-to-use TapToTable Dashboard is designed for restaurant owners, not technical experts. You can update Promotions, Events, Featured Items, customer actions, links, photos, hours, and other restaurant details from your phone or computer. Ongoing support is included.",
  },
  {
    q: "We own several restaurants. Is TapToTable affordable?",
    a: "Yes. Multi-location owners pay $59/month for the first location and $39/month for each additional eligible location under the same ownership. Every location has its own page and dashboard, and you can manage the restaurants independently. Contact TapToTable to arrange multi-location pricing.",
  },
  {
    q: "We are not ready to commit.",
    a: "You do not pay before seeing your page. We build your personalized TapToTable Page first, send you a private review link, make requested changes, and you activate only when you are satisfied.",
  },
];

export default function FAQ() {
  return (
    <>
      <section
        id="faq"
        className="border-t border-white/5 px-6 py-24 md:px-12 md:py-32"
        data-testid="faq-section"
      >
        <div className="mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="label-caps">FAQ</div>

            <h2 className="mt-3 font-display text-2xl font-semibold leading-tight tracking-tight sm:text-3xl lg:text-4xl">
              Questions Before We Build Your Page?
            </h2>

            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-zinc-400 md:text-base">
              Learn how page creation, Promotions, Events, Featured Items,
              multi-location pricing, review, activation, updates, support,
              and customer activity tracking work.
            </p>
          </motion.div>

          <Accordion
            type="single"
            collapsible
            className="mt-10 w-full"
            data-testid="faq-accordion"
          >
            {faqs.map((faq, index) => (
              <AccordionItem
                key={faq.q}
                value={`item-${index}`}
                className="border-b border-white/10"
              >
                <AccordionTrigger
                  className="py-5 text-left font-display text-base font-medium hover:text-[#00E5FF] hover:no-underline md:text-lg"
                  data-testid={`faq-question-${index}`}
                >
                  {faq.q}
                </AccordionTrigger>

                <AccordionContent className="pb-5 text-sm leading-relaxed text-zinc-400 md:text-base">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section
        id="still-deciding"
        className="border-t border-white/5 bg-white/[0.02] px-6 py-24 md:px-12 md:py-32"
        data-testid="still-deciding-section"
      >
        <div className="mx-auto max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="label-caps">Still Deciding?</div>

            <h2 className="mt-3 font-display text-2xl font-semibold leading-tight tracking-tight sm:text-3xl lg:text-4xl">
              Common Concerns From Restaurant Owners
            </h2>

            <p className="mx-auto mt-4 max-w-3xl text-sm leading-relaxed text-zinc-400 md:text-base">
              TapToTable lets you review your personalized page before making
              a decision, then use it for Promotions, Events, or Featured Items
              after activation. Here are straightforward answers to common
              concerns.
            </p>
          </motion.div>

          <div className="mt-10 grid gap-4 md:grid-cols-2">
            {objections.map((item, index) => (
              <motion.article
                key={item.q}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: index * 0.05 }}
                className="rounded-2xl border border-white/10 bg-[#121214] p-6"
              >
                <h3 className="font-display text-lg font-semibold text-white">
                  “{item.q}”
                </h3>

                <p className="mt-3 text-sm leading-relaxed text-zinc-400">
                  {item.a}
                </p>
              </motion.article>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mt-10 rounded-2xl border border-[#00E5FF]/25 bg-[#00E5FF]/10 p-6 text-center md:p-8"
          >
            <h3 className="font-display text-xl font-semibold text-white md:text-2xl">
              Review Your Page Before You Activate
            </h3>

            <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-zinc-300 md:text-base">
              We build your personalized TapToTable Page first. You can review
              it, request changes, and activate only when you are satisfied.
            </p>

            <a
              href="/signup"
              className="mt-6 inline-flex items-center justify-center rounded-full bg-[#00E5FF] px-6 py-3 text-sm font-semibold text-black transition-all hover:bg-[#66FFFF] hover:shadow-[0_0_24px_rgba(0,229,255,0.35)]"
              data-testid="still-deciding-cta"
            >
              Build My TapToTable Page
            </a>
          </motion.div>
        </div>
      </section>
    </>
  );
}
